// Keyboard Shortcuts editor — displays all available shortcuts and allows
// customization. Follows the same pattern as NewDocumentDialog.ts (modal).

export interface ShortcutEntry {
  id: string;
  label: string;
  shortcut: string;
  defaultShortcut: string;
  category: string;
}

export type ShortcutMap = Map<string, string>;

const DEFAULT_SHORTCUTS: ShortcutEntry[] = [
  { id: "move",         label: "Move",               shortcut: "V", defaultShortcut: "V", category: "Tools" },
  { id: "marquee",      label: "Marquee",            shortcut: "M", defaultShortcut: "M", category: "Tools" },
  { id: "lasso",        label: "Lasso",              shortcut: "L", defaultShortcut: "L", category: "Tools" },
  { id: "magic-wand",   label: "Magic Wand",         shortcut: "W", defaultShortcut: "W", category: "Tools" },
  { id: "crop",         label: "Crop",               shortcut: "C", defaultShortcut: "C", category: "Tools" },
  { id: "eyedropper",   label: "Eyedropper",         shortcut: "I", defaultShortcut: "I", category: "Tools" },
  { id: "brush",        label: "Brush",              shortcut: "B", defaultShortcut: "B", category: "Tools" },
  { id: "eraser",       label: "Eraser",             shortcut: "E", defaultShortcut: "E", category: "Tools" },
  { id: "clone-stamp",  label: "Clone Stamp",        shortcut: "S", defaultShortcut: "S", category: "Tools" },
  { id: "gradient",     label: "Gradient",           shortcut: "G", defaultShortcut: "G", category: "Tools" },
  { id: "blur",         label: "Blur / Sharpen",     shortcut: "R", defaultShortcut: "R", category: "Tools" },
  { id: "dodge-burn",   label: "Dodge / Burn",       shortcut: "O", defaultShortcut: "O", category: "Tools" },
  { id: "pen",          label: "Pen",                shortcut: "P", defaultShortcut: "P", category: "Tools" },
  { id: "type",         label: "Type",               shortcut: "T", defaultShortcut: "T", category: "Tools" },
  { id: "shape",        label: "Shape",              shortcut: "U", defaultShortcut: "U", category: "Tools" },
  { id: "hand",         label: "Hand",               shortcut: "H", defaultShortcut: "H", category: "Tools" },
  { id: "zoom",         label: "Zoom",               shortcut: "Z", defaultShortcut: "Z", category: "Tools" },
  { id: "new",          label: "New Document",       shortcut: "Ctrl+N", defaultShortcut: "Ctrl+N", category: "File" },
  { id: "open",         label: "Open",               shortcut: "Ctrl+O", defaultShortcut: "Ctrl+O", category: "File" },
  { id: "save",         label: "Save",               shortcut: "Ctrl+S", defaultShortcut: "Ctrl+S", category: "File" },
  { id: "save-as",      label: "Save As",            shortcut: "Ctrl+Shift+S", defaultShortcut: "Ctrl+Shift+S", category: "File" },
  { id: "close",        label: "Close",              shortcut: "Ctrl+W", defaultShortcut: "Ctrl+W", category: "File" },
  { id: "undo",         label: "Undo",               shortcut: "Ctrl+Z", defaultShortcut: "Ctrl+Z", category: "Edit" },
  { id: "redo",         label: "Redo",               shortcut: "Ctrl+Shift+Z", defaultShortcut: "Ctrl+Shift+Z", category: "Edit" },
  { id: "cut",          label: "Cut",                shortcut: "Ctrl+X", defaultShortcut: "Ctrl+X", category: "Edit" },
  { id: "copy",         label: "Copy",               shortcut: "Ctrl+C", defaultShortcut: "Ctrl+C", category: "Edit" },
  { id: "paste",        label: "Paste",              shortcut: "Ctrl+V", defaultShortcut: "Ctrl+V", category: "Edit" },
  { id: "select-all",   label: "Select All",         shortcut: "Ctrl+A", defaultShortcut: "Ctrl+A", category: "Select" },
  { id: "deselect",     label: "Deselect",           shortcut: "Ctrl+D", defaultShortcut: "Ctrl+D", category: "Select" },
  { id: "inverse",      label: "Inverse",            shortcut: "Ctrl+Shift+I", defaultShortcut: "Ctrl+Shift+I", category: "Select" },
  { id: "reselect",     label: "Reselect",           shortcut: "Ctrl+Shift+D", defaultShortcut: "Ctrl+Shift+D", category: "Select" },
  { id: "quick-mask",   label: "Quick Mask",         shortcut: "Q", defaultShortcut: "Q", category: "Select" },
  { id: "fullscreen",   label: "Cycle Screen Mode",  shortcut: "F", defaultShortcut: "F", category: "View" },
  { id: "fit-screen",   label: "Fit to Screen",      shortcut: "Ctrl+0", defaultShortcut: "Ctrl+0", category: "View" },
  { id: "zoom-in",      label: "Zoom In",            shortcut: "Ctrl++", defaultShortcut: "Ctrl++", category: "View" },
  { id: "zoom-out",     label: "Zoom Out",           shortcut: "Ctrl+-", defaultShortcut: "Ctrl+-", category: "View" },
  { id: "proof-colors", label: "Proof Colors",       shortcut: "Ctrl+Y", defaultShortcut: "Ctrl+Y", category: "View" },
  { id: "gamut",        label: "Gamut Warning",      shortcut: "Ctrl+Shift+Y", defaultShortcut: "Ctrl+Shift+Y", category: "View" },
  { id: "gaussian-blur", label: "Gaussian Blur",     shortcut: "Ctrl+Shift+B", defaultShortcut: "Ctrl+Shift+B", category: "Filter" },
  { id: "last-filter",  label: "Last Filter",        shortcut: "Ctrl+F", defaultShortcut: "Ctrl+F", category: "Filter" },
  { id: "transform",    label: "Free Transform",     shortcut: "Ctrl+T", defaultShortcut: "Ctrl+T", category: "Edit" },
  { id: "duplicate",    label: "Duplicate Layer",    shortcut: "Ctrl+J", defaultShortcut: "Ctrl+J", category: "Layer" },
  { id: "merge-down",   label: "Merge Down",         shortcut: "Ctrl+E", defaultShortcut: "Ctrl+E", category: "Layer" },
  { id: "new-layer",    label: "New Layer",          shortcut: "Ctrl+Shift+N", defaultShortcut: "Ctrl+Shift+N", category: "Layer" },
  { id: "flatten",      label: "Flatten Image",      shortcut: "", defaultShortcut: "", category: "Layer" },
];

export function getDefaultShortcuts(): ShortcutEntry[] {
  return DEFAULT_SHORTCUTS.map(e => ({ ...e }));
}

export function saveShortcutOverrides(map: ShortcutMap): void {
  try {
    localStorage.setItem("pixelpa_shortcuts", JSON.stringify([...map]));
  } catch { /* storage full or unavailable */ }
}

export function loadShortcutOverrides(): ShortcutMap {
  try {
    const raw = localStorage.getItem("pixelpa_shortcuts");
    if (raw) return new Map(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Map();
}

export function applyShortcutOverrides(entries: ShortcutEntry[], overrides: ShortcutMap): ShortcutEntry[] {
  return entries.map(e => {
    const override = overrides.get(e.id);
    if (override !== undefined) {
      return { ...e, shortcut: override };
    }
    return e;
  });
}

export class ShortcutsEditor {
  private root: HTMLDivElement | null = null;
  private built = false;
  private entries: ShortcutEntry[] = [];
  private resolveFn: ((value: boolean) => void) | null = null;
  private editingRow: HTMLElement | null = null;
  private overrides: ShortcutMap = new Map();

  async open(): Promise<boolean> {
    if (!this.built) this.build();
    const defaults = getDefaultShortcuts();
    const saved = loadShortcutOverrides();
    this.entries = applyShortcutOverrides(defaults, saved);
    this.overrides = new Map(saved);
    this.render();
    this.root!.style.display = "";
    return new Promise((resolve) => { this.resolveFn = resolve; });
  }

  close(): void {
    if (this.resolveFn) { this.resolveFn(true); this.resolveFn = null; }
    if (this.root) this.root.style.display = "none";
  }

  getOverrides(): ShortcutMap {
    return new Map(this.overrides);
  }

  private build(): void {
    this.root = document.createElement("div");
    this.root.className = "shortcuts-editor";
    this.root.style.cssText = "position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:10000;display:none;align-items:center;justify-content:center;";
    this.root.innerHTML = `
      <div class="shortcuts-editor__sheet" style="background:var(--bg-secondary);border-radius:2px;max-width:600px;width:90%;max-height:80vh;display:flex;flex-direction:column;">
        <div class="shortcuts-editor__title" style="padding:8px 12px;border-bottom:1px solid var(--bg-tertiary);font-size:11px;font-weight:600;">Keyboard Shortcuts</div>
        <div class="shortcuts-editor__body" style="flex:1;overflow-y:auto;padding:6px;">
          <div class="shortcuts-editor__toolbar" style="display:flex;gap:6px;margin-bottom:6px;">
            <button class="shortcuts-editor__btn" data-action="reset" style="padding:3px 10px;font-size:11px;cursor:pointer;">Reset All</button>
          </div>
          <div class="shortcuts-editor__list"></div>
        </div>
        <div class="shortcuts-editor__actions" style="padding:6px 12px;border-top:1px solid var(--bg-tertiary);display:flex;justify-content:flex-end;gap:8px;">
          <button class="shortcuts-editor__btn shortcuts-editor__btn--primary" data-action="done" style="padding:4px 12px;font-size:11px;cursor:pointer;background:var(--accent);color:#fff;border:none;border-radius:2px;">Done</button>
        </div>
      </div>
    `;
    document.body.appendChild(this.root);
    this.built = true;

    this.root.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-action='done']")) { this.close(); }
      if (target.closest("[data-action='reset']")) {
        this.overrides.clear();
        this.entries = getDefaultShortcuts();
        this.render();
      }
    });

    this.root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { this.close(); }
    });
  }

  private render(): void {
    if (!this.root) return;
    const list = this.root.querySelector(".shortcuts-editor__list");
    if (!list) return;

    const groups = new Map<string, ShortcutEntry[]>();
    for (const e of this.entries) {
      const arr = groups.get(e.category) ?? [];
      arr.push(e);
      groups.set(e.category, arr);
    }

    list.innerHTML = "";
    for (const [cat, items] of groups) {
      const catEl = document.createElement("div");
      catEl.className = "shortcuts-editor__category";
      catEl.innerHTML = `<div class="shortcuts-editor__cat-title" style="font-size:11px;font-weight:600;color:var(--text-dim);padding:8px 0 4px;text-transform:uppercase;">${escapeHtml(cat)}</div>`;
      const table = document.createElement("div");
      table.className = "shortcuts-editor__table";

      for (const item of items) {
        const row = document.createElement("div");
        row.className = "shortcuts-editor__row";
        row.style.cssText = "display:flex;justify-content:space-between;align-items:center;padding:4px 8px;cursor:pointer;border-radius:2px;";
        row.setAttribute("data-id", item.id);
        const isCustom = this.overrides.has(item.id);
        row.innerHTML = `
          <span class="shortcuts-editor__label" style="font-size:12px;">${escapeHtml(item.label)}</span>
          <span class="shortcuts-editor__key" style="font-size:11px;padding:2px 6px;background:var(--bg-tertiary);border-radius:1px;${isCustom ? "color:var(--accent);" : ""}">${escapeHtml(item.shortcut) || "—"}</span>
        `;
        row.addEventListener("click", () => this.startEditing(row, item));
        table.appendChild(row);
      }
      catEl.appendChild(table);
      list.appendChild(catEl);
    }
  }

  private startEditing(row: HTMLElement, entry: ShortcutEntry): void {
    if (this.editingRow) {
      this.finishEditing(this.editingRow);
    }

    this.editingRow = row;
    const keyEl = row.querySelector(".shortcuts-editor__key") as HTMLElement;
    if (!keyEl) return;

    keyEl.textContent = "Press key...";
    keyEl.style.color = "var(--accent)";
    keyEl.style.background = "var(--bg-input)";

    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      let shortcut = "";
      if (e.ctrlKey || e.metaKey) shortcut += "Ctrl+";
      if (e.shiftKey) shortcut += "Shift+";
      if (e.altKey) shortcut += "Alt+";

      const key = e.key;
      if (key === "Escape") {
        this.editingRow = null;
        this.render();
        document.removeEventListener("keydown", handler, true);
        return;
      }
      if (key === "Backspace" || key === "Delete") {
        shortcut = "";
      } else if (key.length === 1) {
        shortcut += key.toUpperCase();
      } else if (key === " ") {
        shortcut += "Space";
      } else if (["Enter", "Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown", "Insert", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12"].includes(key)) {
        shortcut += key;
      } else {
        this.editingRow = null;
        this.render();
        document.removeEventListener("keydown", handler, true);
        return;
      }

      // Check for conflicts
      const conflict = this.entries.find(e => e.id !== entry.id && e.shortcut === shortcut);
      if (conflict && shortcut !== "") {
        if (!confirm(`"${shortcut}" is already assigned to "${conflict.label}". Reassign?`)) {
          this.editingRow = null;
          this.render();
          document.removeEventListener("keydown", handler, true);
          return;
        }
      }

      if (shortcut === "") {
        this.overrides.delete(entry.id);
        entry.shortcut = entry.defaultShortcut;
      } else {
        this.overrides.set(entry.id, shortcut);
        entry.shortcut = shortcut;
      }

      this.editingRow = null;
      this.render();
      document.removeEventListener("keydown", handler, true);
    };

    document.addEventListener("keydown", handler, true);
  }

  private finishEditing(row: HTMLElement): void {
    this.editingRow = null;
    this.render();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));
}
